import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';
import { getCmsApiBaseUrl } from 'src/services/api.config';
import {
  filterOrganizationsWithCustomerId,
  normalizeOrganizationRecord,
  type OrgFlow,
} from 'src/services/organizations.service';
import { getProfileOrgIdsWithCustomerId } from 'src/services/users.service';

export interface OrganizationData {
  id: string;
  name: string;
  display_name: string;
  customerId?: string;
  flowIds?: string[];
  flows?: OrgFlow[];
  branding?: {
    logo_url?: string;
  };
}
interface OrganizationState {
  organizations: Array<OrganizationData>;
  isLoading: boolean;
  selectedOrganization: OrganizationData | null;
  error: string | null;
}

const initialState: OrganizationState = {
  organizations: [
    { id: '', name: 'All Organizations', display_name: 'All Organizations' },
  ],
  selectedOrganization: null,
  isLoading: false,
  error: null,
};

const ALL_ORGANIZATIONS: OrganizationData = {
  id: '',
  name: 'All Organizations',
  display_name: 'All Organizations',
};

export const fetchOrganizations = createAsyncThunk(
  '/organization/fetchOrganizations',
  async (
    { sub, __raw }: { sub: string; __raw: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const state: any = getState();
      const liveData = Boolean(state?.sliceTransaction?.liveData);
      const baseUrl = getCmsApiBaseUrl(liveData);
      const headers = { Authorization: `Bearer ${__raw}` };

      const [orgsRes, profileRes] = await Promise.allSettled([
        axios.get(
          `${baseUrl}/user/organizations/${encodeURIComponent(sub)}`,
          { headers },
        ),
        axios.get(`${baseUrl}/profile`, { headers }),
      ]);

      if (orgsRes.status === 'rejected') {
        throw orgsRes.reason;
      }

      const rawData = orgsRes.value.data?.data || orgsRes.value.data || [];
      const profile =
        profileRes.status === 'fulfilled' ? profileRes.value.data : null;
      const allowedIds = getProfileOrgIdsWithCustomerId(profile);

      const organizationData = filterOrganizationsWithCustomerId(
        Array.isArray(rawData) ? rawData.map(normalizeOrganizationRecord) : [],
        allowedIds,
      );

      return { data: organizationData };
    } catch (error) {
      if (error instanceof AxiosError) {
        const status = error.response?.status;
        if (status === 401)
          return rejectWithValue({
            type: 'unauthorized',
            message: 'Session expired. Please sign out and sign back in.',
          });
        if (status === 403)
          return rejectWithValue({
            type: 'forbidden',
            message: 'You do not have permission to view organizations.',
          });
        if (!error.response)
          return rejectWithValue({
            type: 'network',
            message: 'Network error. Check your connection.',
          });
        return rejectWithValue({ type: 'api', status, message: error.message });
      }
      return rejectWithValue({ type: 'unknown', message: String(error) });
    }
  },
);

export const organizationSlice = createSlice({
  name: 'organization',
  initialState,
  reducers: {
    setSelectedOrganization: (state, action) => {
      state.selectedOrganization = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOrganizations.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        const organizationData = action.payload?.data || [];
        state.isLoading = false;

        if (organizationData.length === 1) {
          state.organizations = organizationData;
          state.selectedOrganization = organizationData[0];
        } else if (organizationData.length === 0) {
          state.organizations = [ALL_ORGANIZATIONS];
          state.selectedOrganization = ALL_ORGANIZATIONS;
        } else {
          state.organizations = [ALL_ORGANIZATIONS, ...organizationData];
          const currentId = state.selectedOrganization?.id ?? '';
          const refreshed = organizationData.find(org => org.id === currentId);
          if (refreshed) {
            state.selectedOrganization = refreshed;
          } else if (!currentId || currentId === '') {
            state.selectedOrganization = ALL_ORGANIZATIONS;
          } else {
            state.selectedOrganization = ALL_ORGANIZATIONS;
          }
        }
        state.error = null;
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.isLoading = false;
        const payload = action.payload as
          | { type: string; message: string }
          | undefined;
        state.error =
          payload?.message ??
          action.error.message ??
          'Failed to fetch organizations';
        state.organizations = [];
      });
  },
});

export const { setSelectedOrganization } = organizationSlice.actions;
export default organizationSlice.reducer;
